import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import axios from "axios";
import PatientSearch from "../../../../Components/PatientSearch";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";

const baseUrl = () => process.env.REACT_APP_API_URL || "";

export default function AddInvoice() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(moment().format("YYYY-MM-DD"));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ item_name: "", quantity: 1, unit_price: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const addLine = () => setLines((prev) => [...prev, { item_name: "", quantity: 1, unit_price: "" }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!patient?.id) {
      setError(t("finance.selectPatient", { defaultValue: "Please select a patient." }));
      return;
    }
    const validLines = lines.filter((l) => l.item_name && l.quantity && l.unit_price !== "");
    if (validLines.length === 0) {
      setError(t("finance.addAtLeastOneLine", { defaultValue: "Add at least one line item." }));
      return;
    }
    setSubmitting(true);
    let invoiceId = null;
    try {
      const invRes = await axios.post(
        `${baseUrl()}/invoices/`,
        {
          patient: patient.id,
          invoice_date: invoiceDate,
          status: 1,
          notes,
        },
        {
          headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" },
          skipGlobalErrorHandler: true,
        }
      );
      invoiceId = invRes.data.id;
      for (const line of validLines) {
        await axios.post(
          `${baseUrl()}/invoice-lines/`,
          {
            invoice: invoiceId,
            item_name: line.item_name,
            quantity: Number(line.quantity),
            unit_price: Number(line.unit_price),
          },
          {
            headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" },
            skipGlobalErrorHandler: true,
          }
        );
      }
      queryClient.invalidateQueries(["invoices"]);
      toast.success(t("finance.invoiceCreated", { defaultValue: "Invoice created." }));
      if (invoiceId) navigate(`/finance/invoices/${invoiceId}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.message);
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/finance/invoices")} sx={{ mb: 2 }}>
        {t("finance.back", { defaultValue: "Back to Invoices" })}
      </Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
          {t("finance.addInvoice", { defaultValue: "Add Invoice" })}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <PatientSearch
                value={patient}
                onChange={setPatient}
                label={t("finance.patient")}
                placeholder={t("finance.search", "Search...")}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("finance.date")}
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label={t("finance.notes")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 2 }}>
            {t("finance.lineItems", { defaultValue: "Line Items" })}
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell>{t("finance.item")}</TableCell>
                  <TableCell align="right" width={120}>{t("finance.quantity")}</TableCell>
                  <TableCell align="right" width={120}>{t("finance.unitPrice")}</TableCell>
                  <TableCell width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={line.item_name}
                        onChange={(e) => updateLine(idx, "item_name", e.target.value)}
                        placeholder={t("finance.itemName", "Item name")}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0.01, step: 1 }}
                        value={line.quantity}
                        onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={line.unit_price}
                        onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addLine} sx={{ mb: 2 }}>
            {t("finance.addLine", { defaultValue: "Add Line" })}
          </Button>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={24} /> : t("finance.save", "Save")}
            </Button>
            <Button variant="outlined" onClick={() => navigate("/finance/invoices")}>
              {t("finance.cancel", "Cancel")}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
